import React from 'react';
import { Row, Col, Form, FormGroup, Label, Input, Card, CardHeader, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Collapse } from 'reactstrap';
import AddressAutocomplete from '../../../AddressAutocomplete';
import { validateBankInfo } from '../validation';
import ConfirmationModal from 'components/ConfirmationModal';
import InputsValidations from '../../../../modules/InputsValidations.js';

function BankInfoStep({ value, onChange, errors = {} , locked = false}) {
  const banks = (value && value.banks) || [];
  const draft = (value && value.draft) || {};

  const [editingIndex, setEditingIndex] = React.useState(-1); // -1 means ADDING.
  const [showEditConfirmationModal, setShowEditConfirmationModal] = React.useState(false);
  const [bankToEditIndex, setBankToEditIndex] = React.useState(null); 
  const [showBankSavedModal, setShowBankSavedModal] = React.useState(false);
  const [lastAction, setLastAction] = React.useState('saved'); // 'saved' or 'updated'
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] = React.useState(false);
  const [bankToRemoveIndex, setBankToRemoveIndex] = React.useState(null);
  
  // State for form visibility. Default to TRUE if no banks exist, otherwise FALSE.
  const [showAddBankForm, setShowAddBankForm] = React.useState(banks.length === 0);

  // New state for Discard/Add New Bank confirmation
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = React.useState(false);


  const setDraft = (nextDraft) => {
    const next = { ...(value || {}), draft: nextDraft };
    onChange && onChange(next);
  };

  const hasDraft = draft && Object.keys(draft).length > 0;
  const draftValidation = validateBankInfo({ banks, draft });
  const draftHasErrors = !!(draftValidation && draftValidation.draft && Object.keys(draftValidation.draft).length > 0);

  // Check if form is being filled
  const isFormBeingFilled = () => {
    if (!hasDraft) return false;
    const requiredFields = ['type', 'bankName', 'accountName', 'accountNumber', 'routingNumber', 'bankAddress'];
    const hasAnyRequiredValue = requiredFields.some(field => String(draft[field] || '').trim() !== '');
    return hasAnyRequiredValue;
  };

  const addBank = () => {
    // 1. Validate draft before saving/updating
    const draftValidation = validateBankInfo({ banks: [], draft });
    if (draftValidation && draftValidation.draft && Object.keys(draftValidation.draft).length > 0) {
      return;
    }

    let nextBanks;
    let action;

    if (editingIndex !== -1) {
      nextBanks = banks.map((bank, i) => (i === editingIndex ? { ...draft } : bank));
      action = 'updated';
    } else {
      nextBanks = [...banks, { ...draft }];
      action = 'saved';
    }

    const next = { banks: nextBanks, draft: {} };
    onChange && onChange(next);
    
    setEditingIndex(-1);
    setLastAction(action);
    setShowBankSavedModal(true);
    // NEW: Hide the form after successful save/update
    setShowAddBankForm(false);
  };

  const handleBankEdit = (index) => {
    // Check if the current draft is dirty AND we're not editing the bank already in the draft
    if (isFormBeingFilled() && index !== editingIndex) {
      setBankToEditIndex(index);
      setShowEditConfirmationModal(true);
    } else {
      confirmEditBank(index);
    }
    // NEW: Always show the form when editing starts
    setShowAddBankForm(true);
  };

  // Logic executed after confirmation modal or if no changes exist
  const confirmEditBank = (index) => {
    const bankToEdit = banks[index];
    if (bankToEdit) {
      setDraft(bankToEdit);
      setEditingIndex(index);
      setBankToEditIndex(null);
      setShowEditConfirmationModal(false);
    }
  };

  const handleRemoveBankClick = (idx) => {
    setBankToRemoveIndex(idx);
    setShowRemoveConfirmationModal(true);
  };

  const confirmRemoveBank = () => {
    if (bankToRemoveIndex !== null) {
        if (bankToRemoveIndex === editingIndex) {
            setDraft({});
            setEditingIndex(-1);
            // If the deleted bank was the one being edited, hide the form
            setShowAddBankForm(false);
        }
        
        const nextBanks = banks.filter((_, i) => i !== bankToRemoveIndex);
        onChange && onChange({ ...(value || {}), banks: nextBanks });
    }
    
    setShowRemoveConfirmationModal(false);
    setBankToRemoveIndex(null);
  };
  
  const startNewBankEntry = () => {
    // If cancelling an edit or clearing a dirty form
    if (isFormBeingFilled()) {
       setConfirmOpen(true);
    } else {
      setDraft({});
      setEditingIndex(-1);
      // Optional: Hide form if they just cleared an empty draft
      if (banks.length > 0) setShowAddBankForm(false); 
    }
  };
  
  // NEW: Handle the Discard & Add New button click
  const handleDiscardBank = () => {
    if (isFormBeingFilled()) {
        setShowDiscardConfirmationModal(true);
    } else {
        // If draft is empty, just reset and focus on adding new
        setDraft({});
        setEditingIndex(-1);
        setShowAddBankForm(false);
    }
  };

  const confirmDiscardBank = () => {
    setDraft({});
    setEditingIndex(-1);
    setShowDiscardConfirmationModal(false);
    setShowAddBankForm(false);
  };


  const [confirmOpen, setConfirmOpen] = React.useState(false);

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      {banks && banks.length > 0 && (
        <>
          <div>
            <h2>
              Banks ({banks.length})
            </h2>

            <ul className='list-group my-2'>
              {banks.map((bank, index) => (
                <li 
                    key={index} 
                    className={`list-group-item d-flex justify-content-between align-items-center ${index === editingIndex ? 'border-primary border-top' : ''}`}
                >
                  <div className="flex-grow-1">
                    <h4 className='text-primary'>{bank.type || 'BANK'} <span className='text-dark mx-1'>•</span> {bank.bankName}</h4>
                    <div>
                      <span>
                        <b>Account Name:</b> {bank.accountName} <span className='mx-2'>|</span> 
                      </span>
                      <span>
                        <b>Account Number:</b> ****{(bank.accountNumber || bank.number || '').slice(-4)} <span className='mx-2'>|</span> 
                      </span>
                      <span>
                        <b>Routing Number:</b> *****{(bank.routingNumber || '').slice(-4)}
                      </span>
                      <p className='mb-0'>
                        {bank.bankAddress}
                      </p>
                    </div>
                  </div>

                  <div className="btn-group">
                    <Button 
                      color="primary"
                      size='md'
                      onClick={() => handleBankEdit(index)}
                      disabled={locked || index === editingIndex}
                      >
                      <i className='ni ni-settings-gear-65'></i>
                    </Button>
                    <Button 
                      color="secondary" 
                      size='md'
                      onClick={() => handleRemoveBankClick(index)}
                      disabled={locked || index === editingIndex}> 
                        <i className='ni ni-fat-remove'></i>
                    </Button>
                  </div>
                </li>
                ))}
            </ul>
          </div>

          <hr></hr>
        </>
      )}

      <Collapse isOpen={showAddBankForm}>
        { banks.length >= 1 && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>
              {editingIndex !== -1 ? 'Editing Bank' : 'Add Another Bank'}
            </h2>
          </div>
        ) }

        <Row className='mb-2'>
          <Col md="12">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="text-primary">
                <i className="ni ni-credit-card mr-2"></i>
                Bank Information
              </h3>
            </div>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Type <span>*</span></Label>
              <Input
                type="select"
                value={draft.type || ''}
                onChange={(e) => {
                  const next = { ...draft, type: e.target.value };
                  setDraft(next);
                }}
                className={(errors.draft?.type) ? 'is-invalid' : ''}
                bsSize="sm"
              >
                <option value="">Select type</option>
                <option value="WIREBANK">WIREBANK</option>
                <option value="ACH">ACH</option>
              </Input>
              {errors.draft?.type && (
                <div className="invalid-feedback d-block">{errors.draft.type}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Bank Name <span>*</span></Label>
              <Input 
                type="text" 
                value={draft.bankName || ''} 
                onChange={(e) => {
                  const next = { ...draft, bankName: e.target.value.slice(0, 255) };
                  setDraft(next);
                }}
                placeholder="Enter bank name" 
                maxLength={255}
                className={(errors.draft?.bankName) ? 'is-invalid' : ''}
                bsSize="sm"
              />
              {errors.draft?.bankName && (
                <div className="invalid-feedback d-block">{errors.draft.bankName}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Account Name <span>*</span></Label>
              <Input 
                type="text" 
                value={draft.accountName || ''} 
                onChange={(e) => {
                  const next = { ...draft, accountName: e.target.value.slice(0, 255) };
                  setDraft(next);
                }}
                maxLength={255}
                className={(errors.draft?.accountName) ? 'is-invalid' : ''}
                bsSize="sm"
                placeholder='Enter account name'
              />
              {errors.draft?.accountName && (
                <div className="invalid-feedback d-block">{errors.draft.accountName}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Account Number <span>*</span></Label>
              <Input 
                type="text" 
                value={draft.accountNumber || ''} 
                onChange={(e) => {
                  const filtered = InputsValidations.filterAccountNumber(e.target.value);
                  const next = { ...draft, accountNumber: filtered };
                  setDraft(next);
                }}
                placeholder="Enter account number"
                maxLength={19}
                className={(errors.draft?.accountNumber) ? 'is-invalid' : ''}
                bsSize="sm"
              />
              {errors.draft?.accountNumber && (
                <div className="invalid-feedback d-block">{errors.draft.accountNumber}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Routing Number <span>*</span></Label>
              <Input 
                type="text" 
                value={draft.routingNumber || ''} 
                onChange={(e) => {
                  const filtered = InputsValidations.filterRoutingNumber(e.target.value);
                  const next = { ...draft, routingNumber: filtered };
                  setDraft(next);
                }}
                placeholder="9-digit routing number"
                maxLength={9}
                className={(errors.draft?.routingNumber) ? 'is-invalid' : ''}
                bsSize="sm"
              />
              {errors.draft?.routingNumber && (
                <div className="invalid-feedback d-block">{errors.draft.routingNumber}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Bank Address <span>*</span></Label>
              <AddressAutocomplete
                value={draft.bankAddress || ''} 
                onChange={(text) => {
                  const next = {
                    ...draft,
                    bankAddress: text,
                    bankCity: '',
                    bankState: '',
                    bankCountry: ''
                  };
                  setDraft(next);
                }}
                onSelect={(item) => {
                  const label = item?.label || '';
                  const details = item?.details || {};
                  const next = {
                    ...draft,
                    bankAddress: label,
                    bankCity: details.city || '',
                    bankState: details.state || '',
                    bankCountry: details.country || ''
                  };
                  setDraft(next);
                }}
                placeholder="Start typing bank address..."
                className={(errors.draft?.bankAddress) ? 'is-invalid' : ''}
                bsSize="sm"
              />
              {errors.draft?.bankAddress && (
                <div className="invalid-feedback d-block">{errors.draft.bankAddress}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg="6" xl="4" className="mb-2 d-none">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Bank City</Label>
              <Input 
                type="text" 
                value={draft.bankCity || ''} 
                readOnly
                disabled
                className="bg-light border-secondary"
                placeholder="Bank city"
                bsSize="sm"
              />
            </FormGroup>
          </Col>
          <Col lg='6' xl="4" className="mb-2 d-none">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Bank State</Label>
              <Input 
                type="text" 
                value={draft.bankState || ''} 
                readOnly
                disabled
                className="bg-light border-secondary"
                placeholder="Bank state"
                bsSize="sm"
              />
            </FormGroup>
          </Col>
          <Col lg='6' xl="4" className="mb-2 d-none">
            <FormGroup className="m-0 p-0">
              <Label className="mb-1">Bank Country</Label>
              <Input 
                type="text" 
                value={draft.bankCountry || ''}
                readOnly
                disabled
                className="bg-light border-secondary"
                placeholder="Bank country"
                bsSize="sm"
              />
            </FormGroup>
          </Col>
        </Row>        
      </Collapse>
      
      <Row className="justify-content-center mt-2">
        <div className='btn-group'>
          {showAddBankForm // if
          ? (
            <>
              <Button
                color="primary" 
                size="md" 
                onClick={addBank} 
                disabled={locked || !isFormBeingFilled() || draftHasErrors}>
                <i className="ni ni-check-bold mr-2" />
                {editingIndex !== -1 ? 'Update Bank' : 'Save Bank'}
              </Button>
              
              { banks.length >= 1 && editingIndex < 0 // if
              ? ( // then
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleDiscardBank}>
                    <i className="ni ni-fat-remove mr-1" />
                    Discard
                </Button>
              ) : ( // else
                <></>
              )}
            </>
          ) : ( // else
              <Button
                color="primary"
                onClick={() => setShowAddBankForm(true)}>
                  <i className="ni ni-fat-add mr-2" />
                  Add New Bank
              </Button>
          )}
            
          {editingIndex !== -1 && (
            <Button
                color="secondary" 
                size="md"
                onClick={startNewBankEntry}>
                <i className="ni ni-fat-remove mr-2" />
                Cancel Edition
            </Button>
          )}
        </div>
      </Row>

      <hr></hr>

      <ConfirmationModal
        isOpen={confirmOpen}
        toggle={() => setConfirmOpen(false)}
        title={'Discard Unsaved Changes?'}
        message={'You have unsaved bank information. If you proceed, these changes will be lost. Do you really want to clear this form?'}
        onConfirm={() => { setDraft({}); setConfirmOpen(false); setEditingIndex(-1); setShowAddBankForm(banks.length === 0); }}
        onCancel={() => setConfirmOpen(false)}
        confirmText={'Yes, Discard'}
        cancelText={'No'}
        confirmColor={'danger'}
        cancelColor={'secondary'}
        icon={'ni ni-fat-remove'}
      />
      
      <Modal
        isOpen={showDiscardConfirmationModal} 
        toggle={() => setShowDiscardConfirmationModal(false)} 
        size="sm"
        centered
      >
        <ModalHeader toggle={() => setShowDiscardConfirmationModal(false)}>
            <i className="ni ni-fat-remove mr-2" />
            Discard Unsaved Changes?
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            You have unsaved bank information. If you proceed, these changes will be lost. Do you really want to clear this form?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="danger" 
            onClick={confirmDiscardBank}>
              <i className='ni ni-fat-remove mr-2'></i>
              Yes, Discard
          </Button>
          <Button color="secondary" onClick={() => setShowDiscardConfirmationModal(false)}>
            No
          </Button>
        </ModalFooter>
      </Modal>

      <Modal 
        isOpen={showEditConfirmationModal} 
        toggle={() => setShowEditConfirmationModal(false)} 
        size="md" 
        centered
      >
        <ModalHeader toggle={() => setShowEditConfirmationModal(false)}>
            <i className="ni ni-notification-70 mr-2" />
            Confirm Edit Bank
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            You have unsaved changes in the form. If you continue to edit a new bank, 
            your current form data will be lost.
          </p>
          {bankToEditIndex !== null && (
            <p className="mb-0">
              <strong>Bank to edit:</strong> {banks[bankToEditIndex]?.bankName}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEditConfirmationModal(false)}>
            <i className="ni ni-fat-remove mr-2" />
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={() => confirmEditBank(bankToEditIndex)}>
            <i className="ni ni-settings-gear-65 mr-2" />
            Continue Edit
          </Button>
        </ModalFooter>
      </Modal>

      <Modal 
        isOpen={showBankSavedModal} 
        toggle={() => setShowBankSavedModal(false)} 
        size="sm" 
        centered
      >
        <ModalHeader toggle={() => setShowBankSavedModal(false)}>
          <i className="ni ni-check-bold mr-2" />
          Bank {lastAction === 'updated' ? 'Updated' : 'Saved'}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {lastAction === 'updated' ? (
              <>The bank information has been successfully updated.</>
            ) : (
              <>The new bank has been successfully added to the list.</>
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setShowBankSavedModal(false)}>
            <i className="ni ni-check-bold mr-2" />
            OK
          </Button>
        </ModalFooter>
      </Modal>
      
      <Modal 
        isOpen={showRemoveConfirmationModal} 
        toggle={() => setShowRemoveConfirmationModal(false)} 
        size="md" 
        centered
      >
        <ModalHeader toggle={() => setShowRemoveConfirmationModal(false)}>
            <i className="ni ni-fat-remove mr-2" />
            Confirm Bank Removal
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            Are you sure you want to remove this bank account?
          </p>
          {bankToRemoveIndex !== null && (
            <p className="mb-0">
              <strong>Bank to remove:</strong> {banks[bankToRemoveIndex]?.bankName} ({banks[bankToRemoveIndex]?.type})
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="danger" 
            onClick={confirmRemoveBank}>
            <i className="ni ni-check-bold mr-2" />
            Remove Bank
          </Button>
          <Button 
            color="secondary" 
            onClick={() => setShowRemoveConfirmationModal(false)}>
            <i className="ni ni-fat-remove mr-2" />
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

    </Form>
  );
}

export default BankInfoStep;